import { VerificationStatus } from "shared/types";
import { has } from "shared/utils";
import { Cell } from "views/applications-id/components/ApplicantsTable/components/Cell";
import {
  NO_CREDIT_FILE,
  ReportStatusBadge
} from "views/applications-id/components/ApplicantsTable/components/ReportStatusBadge";
import {
  ApplicantOrInvitee,
  Verification
} from "views/applications-id/components/ApplicantsTable/types";
import {
  calculatePastDueAccounts,
  hasNoCreditFile,
  toMoneyIfExist
} from "views/applications-id/components/ApplicantsTable/utils";
import { useAuthContext } from "providers/Auth";
import { DataWithAuditFlag } from "./components/DataWithAuditFlag";
import { useCallback } from "react";
import { Applicant } from "types/application";

export const CreditReportSection = ({
  applicant,
  scoreBased,
  verification,
  isApplicant,
  ruleBased,
  inProgress,
  hasAuditFlagEnabled
}: {
  applicant: ApplicantOrInvitee;
  scoreBased?: boolean;
  isApplicant: boolean;
  hasAuditFlagEnabled: boolean;
  ruleBased?: boolean;
  inProgress: boolean;
  verification: Verification;
}) => {
  const { permissions } = useAuthContext();
  const pastDueAccounts = calculatePastDueAccounts(verification.summary);
  const noCreditFile = hasNoCreditFile(verification.summary);

  const getCheckPropValue = useCallback(
    ({
      checkSetting,
      additionalCondition
    }: {
      checkSetting?: { importance?: number | null; check?: boolean };
      additionalCondition?: boolean;
    }): boolean | null => {
      if (verification.status === VerificationStatus.NoDataAvailable)
        return null;

      if (additionalCondition === false) return null;

      if (
        isApplicant &&
        !ruleBased &&
        !inProgress &&
        typeof checkSetting?.importance === "number"
      )
        return checkSetting.check ?? null;

      return null;
    },
    [verification.status, isApplicant, ruleBased, inProgress, applicant]
  );

  const auditSummaryFlags =
    "audit_summary_flags" in applicant
      ? applicant.audit_summary_flags
      : undefined;

  return (
    <>
      <Cell
        isFirst
        isDataCell
        check={getCheckPropValue({
          checkSetting: applicant.criteria_check_settings?.credit_score,
          additionalCondition: !noCreditFile
        })}
        className="relative"
        isPermitted={permissions.canAccessReports.creditReport}
        showRestrictedFlag={!permissions.canAccessReports.creditReport}
      >
        <DataWithAuditFlag
          hasData={has(verification.summary?.score)}
          dataValue={verification.summary?.score}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={auditSummaryFlags}
        />
        <ReportStatusBadge
          verification={verification}
          applicant={applicant as Applicant}
          overrideStatus={noCreditFile ? NO_CREDIT_FILE : undefined}
        />
      </Cell>
      <Cell
        isDataCell
        check={getCheckPropValue({
          checkSetting: applicant.criteria_check_settings?.past_due_account,
          additionalCondition: pastDueAccounts !== "No data"
        })}
        isPermitted={permissions.canAccessReports.creditReport}
      >
        <DataWithAuditFlag
          hasData={has(verification.summary)}
          dataValue={calculatePastDueAccounts(verification.summary)}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={auditSummaryFlags}
        />
      </Cell>
      <Cell
        isDataCell
        check={getCheckPropValue({
          checkSetting:
            applicant.criteria_check_settings?.unpaid_collections_balance,
          additionalCondition:
            verification.summary?.balance_past_due !== "No data"
        })}
        isPermitted={permissions.canAccessReports.creditReport}
      >
        <DataWithAuditFlag
          hasData={
            has(verification.summary?.balance_past_due) &&
            verification.summary.balance_past_due !== "No data"
          }
          dataValue={
            verification.summary?.balance_past_due &&
            verification.summary.balance_past_due !== "No data"
              ? toMoneyIfExist(verification.summary.balance_past_due.cents)
              : null
          }
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={auditSummaryFlags}
          fallbackValue="No data"
        />
      </Cell>
      <Cell
        isDataCell
        check={getCheckPropValue({
          checkSetting: applicant.criteria_check_settings?.collections_balance
        })}
        isPermitted={permissions.canAccessReports.creditReport}
      >
        <DataWithAuditFlag
          hasData={
            has(verification.summary?.balance_of_collections) &&
            verification.summary.balance_of_collections !== "No data"
          }
          dataValue={
            verification.summary?.balance_of_collections &&
            verification.summary.balance_of_collections !== "No data"
              ? toMoneyIfExist(
                  verification.summary.balance_of_collections.cents
                )
              : null
          }
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={auditSummaryFlags}
          fallbackValue="No data"
        />
      </Cell>
      <Cell
        isDataCell
        check={
          inProgress ||
          verification.status === VerificationStatus.Failed ||
          verification.summary?.bankruptcy === "No data"
            ? null
            : getCheckPropValue({
                checkSetting:
                  applicant.criteria_check_settings?.bankruptcy_records
              })
        }
        isPermitted={permissions.canAccessReports.creditReport}
      >
        <DataWithAuditFlag
          hasData={has(verification.summary?.bankruptcy)}
          dataValue={verification.summary?.bankruptcy}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={auditSummaryFlags}
        />
        {"\n"}
        {scoreBased &&
        verification.summary?.bankruptcy !== "No data" &&
        "template_criteria_settings" in applicant &&
        applicant.template_criteria_settings?.bankruptcy_records
          ? `(${applicant.template_criteria_settings.bankruptcy_records.value} within ${applicant.template_criteria_settings.bankruptcy_records.value} years)`
          : null}
      </Cell>
    </>
  );
};
